import { useEffect, useState, useCallback } from 'react';
import { claimsAPI } from '../../services/api';

const CLAIM_TYPE_LABEL = { death: 'Death', disability: 'Disability', other: 'Other' };

function formatMoney(v) {
  return 'TZS ' + Number(v || 0).toLocaleString();
}
function formatDate(dt) {
  if (!dt) return '—';
  return dt.slice(0, 10);
}
function relLabel(r) {
  return r ? r.charAt(0).toUpperCase() + r.slice(1).toLowerCase() : '—';
}

export default function PayoutRegister() {
  const [records, setRecords]         = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [page, setPage]               = useState(1);
  const [pages, setPages]             = useState(1);
  const [search, setSearch]           = useState('');
  const [query, setQuery]             = useState('');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await claimsAPI.payoutRegister(page, query);
      setRecords(res.records || []);
      setTotalCount(res.total || 0);
      setTotalAmount(res.total_amount || 0);
      setPages(res.pages || 1);
    } catch {
      setError('Failed to load payout register.');
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [query]);

  function handleSearch(e) {
    e.preventDefault();
    setQuery(search.trim());
  }

  return (
    <div className="space-y-3">

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Payout Register</h1>
        <p className="text-xs text-slate-500 mt-0.5">Record of all welfare benefit payments made to beneficiaries.</p>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 gap-3">
        <SumCard
          label="Total Payouts"
          value={String(totalCount)}
          sub="beneficiaries paid"
          color="emerald"
          icon="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
        />
        <SumCard
          label="Total Amount"
          value={formatMoney(totalAmount)}
          sub="all time"
          color="orange"
          icon="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by member, beneficiary, or claim no…"
          className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
        />
        <button type="submit"
          className="rounded-xl bg-[#1b2857] px-4 py-2 text-xs font-semibold text-white hover:bg-[#232f6a] transition-colors shadow-sm">
          Search
        </button>
        {query && (
          <button type="button" onClick={() => { setSearch(''); setQuery(''); }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 shadow-sm">
            Clear
          </button>
        )}
      </form>

      {/* Table / cards */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">

        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-400 text-sm">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="px-5 py-8 text-center text-sm text-red-600">{error}</div>
        )}

        {!loading && !error && records.length === 0 && (
          <div className="px-5 py-12 text-center">
            <p className="text-slate-400 text-sm">{query ? 'No results found.' : 'No payouts recorded yet.'}</p>
          </div>
        )}

        {!loading && !error && records.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    {['Claim No', 'Member', 'Beneficiary', 'Relationship', 'Type', 'Amount', 'Payment Date'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400 last:text-right">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {records.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs font-semibold text-slate-700">{r.claim_no}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800 text-sm">{r.member_name}</p>
                        <p className="text-xs text-slate-400">Roll {r.roll_no}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800 text-sm">{r.beneficiary_name}</p>
                        {r.phone && <p className="text-xs text-slate-400">{r.phone}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-slate-700">
                          {relLabel(r.relationship)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
                          {CLAIM_TYPE_LABEL[r.claim_type] || r.claim_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-emerald-700">{formatMoney(r.amount)}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(r.payment_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {records.map((r) => (
                <div key={r.id} className="px-4 py-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{r.beneficiary_name}</p>
                      <p className="text-xs text-slate-500">{relLabel(r.relationship)} of {r.member_name}</p>
                    </div>
                    <span className="shrink-0 text-sm font-bold text-emerald-700">{formatMoney(r.amount)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-slate-600">{r.claim_no}</span>
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-violet-700 font-semibold">{CLAIM_TYPE_LABEL[r.claim_type] || r.claim_type}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{formatDate(r.payment_date)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <p className="text-xs text-slate-400">Page {page} of {pages} · {totalCount} records</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 shadow-sm">
                    ← Prev
                  </button>
                  <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                    className="rounded-xl bg-[#1b2857] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#232f6a] disabled:opacity-40 shadow-sm">
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SumCard({ label, value, sub, color, icon }) {
  const colors = {
    emerald: { shell: 'border-emerald-200/60 bg-[linear-gradient(145deg,rgba(236,253,245,0.92),rgba(255,255,255,0.96))]', icon: 'bg-emerald-500 text-white' },
    orange:  { shell: 'border-orange-200/60 bg-[linear-gradient(145deg,rgba(255,247,237,0.92),rgba(255,255,255,0.96))]', icon: 'bg-[#f07415] text-white'  },
  };
  const c = colors[color] || colors.emerald;
  return (
    <div className={`rounded-2xl border p-4 shadow-[0_4px_16px_rgba(15,23,42,0.05)] ${c.shell}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${c.icon}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
          <p className="mt-0.5 text-base font-bold tracking-tight text-slate-900 leading-tight truncate">{value}</p>
          <p className="text-[11px] text-slate-400">{sub}</p>
        </div>
      </div>
    </div>
  );
}
